import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './app.css';
import * as messageInterceptor from './utils/message-interceptor';
import * as logger from './utils/logger';
import * as player from './services/player';
import * as seek from './services/seek';
import * as parental from './services/parental-control';
import * as audiosubs from './services/audio-subtitles';
import * as youbora from './services/youbora';
import * as resume from './services/resume';
import * as config from './services/config';
import * as gvp from './services/gvp';
import { addEvent, setTitle, getTitle, sendEvents } from './services/bi';
import { useTranslation } from './hooks/translate';
import { Splash } from './views/splash';
import { ReadyToCast } from './views/ready-to-cast';
import { Error } from './views/error';
import { PlayerBar } from './views/player-bar';
import { ReadyToPin } from './views/ready-to-pin';
import { GoToLive } from './views/go-to-live';
import { messages, streamType, OFF, playerStates, eventsBI, inactivity } from './constants';
import { validateSchema } from './services/schema-validator';
import { ROUTES } from './constants/routes';
import { ERROR_TYPES } from './constants/errors';

const MANIFEST_ERROR = {
  expired: 418,
  wrong: 403
};

export const App = () => {
  const [translate] = useTranslation();
  const [skin] = useState(() => {
    switch (process.env.COUNTRY) {
      case 'pt-br':
        return 'Vivo';
      case 'de-de':
        return 'O2';
      default:
        return 'MovistarPlay';
    }
  });
  const [miniguide, setMiniguide] = useState(playerStates.loading);
  const [error, setError] = useState({});
  const [route, setRoute] = useState(ROUTES.splash);
  const [content, setContent] = useState();
  const customData = useRef({});
  const prevCustomData = useRef({}); // Needed because MEDIA_FINISHED happens after new load request
  const bufferingTimeout = useRef(null);
  const closeTimeout = useRef(null);
  const stopTimeout = useRef(null);
  const isPause = useRef(false);
  const isLoadedData = useRef(false);
  const isError7000 = useRef(false);
  const isError3016PipelineDecode = useRef(false);
  const isPinRequired = useRef(false);
  const loadRequest = useRef({}); // In case of 6003 -> Reload
  const startHeartbeatTimeout = useRef();
  const context = cast.framework.CastReceiverContext.getInstance();
  const castDebugLogger = cast.debug.CastDebugLogger.getInstance();
  const playerManager = context.getPlayerManager();
  const log = logger.create('App');

  useEffect(() => {
    try {
      if (process.env.LOGS_DISABLED) {
        logger.disable();
        context.setLoggerLevel(cast.framework.LoggerLevel.NONE);
        castDebugLogger.setEnabled(false);
        castDebugLogger.showDebugLogs(false);
      } else {
        // Add debug overlay
        castDebugLogger.loggerLevelByTags = {
          [logger.LOG_TAG]: cast.framework.LoggerLevel.WARNING
        };
        castDebugLogger.setEnabled(true);
        castDebugLogger.showDebugLogs(true);
        log.warn('Logs ENABLED');
      }
    } catch (error) {
      log.error('Error setting logs. Context not created.');
    }
  }, []);

  useEffect(() => {
    // GO-31873: Reported to Google. Google won't fix it, too lazy.
    setTimeout(() => {
      // hack to hide media controls UI which are shown on GoogleTV:
      const controls = document.querySelector('touch-controls');
      if (controls && controls.shadowRoot) {
        const subDiv = controls.shadowRoot.querySelector('div');
        if (subDiv) {
          subDiv.innerHTML = '';
        }
      }
    }, 5000);
  }, []);

  useEffect(() => {
    // Custom messages.
    messageInterceptor.addCustomListener(messages.gvpConfig, async (data) => {
      validateSchema('gvpConfig', data);

      if (!config.isSameGvpConfig(data)) {
        playerManager.stop();
        config.isInitialGvpConfig() && setRoute(ROUTES.splash);
        config.setGvpData(data);
        // New config must destroy old Youbora plugin.
        youbora.destroy();
        try {
          await gvp.init();
        } catch {
          handleError(ERROR_TYPES.gvpLoadingData);
        }
        try {
          const [uaTime] = await Promise.all([
            gvp.getUniapiTime(),
            gvp.getInstanceSettings(),
            gvp.getFutKeyValues(data.deviceId, data.deviceType)
          ]);
          performance.mark('uaDelay', {
            detail: { delay: seek.toMilliSeconds(uaTime) - Date.now() }
          });
          setRoute(ROUTES.readyToCast);
        } catch {
          handleError(ERROR_TYPES.generic);
        }
      } else {
        messageInterceptor.broadcast(messages.readyToCast);
      }
    });
    messageInterceptor.addCustomListener(messages.seekLive, (value) => {
      validateSchema('seekLive', value);
      seek.onSeekLive(value.time);
    });
    messageInterceptor.addCustomListener(messages.launchStartOver, seek.onLaunchStartOver);
    messageInterceptor.addCustomListener(messages.launchLive, seek.onLaunchLive);
    messageInterceptor.addCustomListener(messages.requestPinSuccess, handleParental);
    messageInterceptor.addCustomListener(messages.disableSubtitle, () => {
      audiosubs.disableSubtitle();
      addEvent(customData.current, eventsBI.subtitle, { ISO: OFF });
    });
    messageInterceptor.addCustomListener(messages.enableSubtitle, (data) => {
      validateSchema('enableSubtitle', data);
      // iOS "enableSubtitle" message with "zxx"
      data.language === OFF ? audiosubs.disableSubtitle() : audiosubs.enableSubtitle(data.language);
      addEvent(customData.current, eventsBI.subtitle, { ISO: data.language });
    });
    messageInterceptor.addCustomListener(messages.changeAudio, (data) => {
      validateSchema('changeAudio', data);
      audiosubs.changeAudio(data.language);
      addEvent(customData.current, eventsBI.audio, { ISO: data.language });
    });
    messageInterceptor.addCustomListener(messages.showMiniguide, () => {
      setMiniguide(playerStates.miniguide);
    });

    // Standard messages.
    messageInterceptor.addListener(cast.framework.messages.MessageType.LOAD, onLoad);
    messageInterceptor.addListener(cast.framework.messages.MessageType.STOP, onStop);
    messageInterceptor.addListener(cast.framework.messages.MessageType.GET_STATUS, (data) => {
      if (isLoadedData.current) {
        log.info('Request status current tracks:', audiosubs.getCurrentTracks());
        audiosubs.sendCurrentTracks();
      }
      playerManager.broadcastStatus(true, data.requestId);
    });
    messageInterceptor.addListener(cast.framework.messages.MessageType.MEDIA_STATUS, setCustomData);

    playerManager.addEventListener(cast.framework.events.EventType.PLAYING, () => {
      youbora.sendRealJoinTime();
    });
    playerManager.addEventListener(cast.framework.events.EventType.PLAYING, () => {
      setMiniguide(playerStates.playing);
      addEvent(customData.current, isPause.current ? eventsBI.resume : eventsBI.play);
      clearTimeout(stopTimeout.current);
      isPause.current = false;
    });
    playerManager.addEventListener(cast.framework.events.EventType.PAUSE, () => {
      isPause.current = true;
      setMiniguide(playerStates.paused);
      addEvent(customData.current, eventsBI.stop);
      setStopTimeout(inactivity.paused, true);
    });
    playerManager.addEventListener(cast.framework.events.EventType.BUFFERING, (e) => {
      if (e.isBuffering) {
        setMiniguide(playerStates.buffering);
        setStopTimeout(inactivity.buffering);
        if (!bufferingTimeout.current) {
          bufferingTimeout.current = setTimeout(
            () => addEvent(customData.current, eventsBI.buffering),
            seek.toMilliSeconds(3)
          );
        }
      } else {
        if (
          isLoadedData.current &&
          customData.current.type === streamType.live &&
          customData.current.liveStartTime
        ) {
          isLoadedData.current = false;
          // Live content on live-edge won't be seeked because devices won't send liveStartTime.
          customData.current.liveStartTime && seek.onSeekLive(customData.current.liveStartTime);
        }
        cancelBufferingEvent();
        clearTimeout(stopTimeout.current);
      }
    });
    playerManager.addEventListener(cast.framework.events.EventType.SEEKING, () =>
      setMiniguide(playerStates.seeking)
    );
    playerManager.addEventListener(cast.framework.events.EventType.PLAYER_LOADING, () => {
      setMiniguide(playerStates.loading);
      setStopTimeout(inactivity.loading);
    });

    playerManager.addEventListener(
      cast.framework.events.EventType.LOADED_DATA,
      () => (isLoadedData.current = true)
    );
    playerManager.addEventListener(cast.framework.events.EventType.ERROR, (e) => {
      if (e.detailedErrorCode === cast.framework.events.DetailedErrorCode.LOAD_FAILED) {
        // Load interrupted by user zapping.
        isError7000.current = true;
      } else if (
        e.detailedErrorCode === cast.framework.events.DetailedErrorCode.MEDIA_UNKNOWN &&
        e.error?.shakaErrorData?.length === 3 &&
        e.error.shakaErrorData[2] === 'PIPELINE_ERROR_DECODE: video decoder reinitialization failed'
      ) {
        // TODO: GO-32202 remove when https://issuetracker.google.com/u/1/issues/375959956 is fixed.
        log.error('PIPELINE_ERROR_DECODE');
        isError3016PipelineDecode.current = true;
      } else {
        const [, httpErrorCode] = e.error?.shakaErrorData || [];
        const text =
          httpErrorCode === MANIFEST_ERROR.expired
            ? ERROR_TYPES.blockUserExpired
            : httpErrorCode === MANIFEST_ERROR.wrong
            ? ERROR_TYPES.blockUserWrong
            : ERROR_TYPES.gvpGeneric;
        handleError(text);
      }
    });

    playerManager.addEventListener(cast.framework.events.EventType.MEDIA_FINISHED, async (e) => {
      isPause.current = false;
      cancelBufferingEvent();
      if (
        e.endedReason === cast.framework.events.EndedReason.ERROR &&
        isError3016PipelineDecode.current
      ) {
        // TODO: GO-32202 remove when https://issuetracker.google.com/u/1/issues/375959956 is fixed.
        isError3016PipelineDecode.current = false;
        playerManager.stop();
        setTimeout(() => {
          playerManager.load(loadRequest.current);
        }, 500);
      } else if (e.endedReason !== cast.framework.events.EndedReason.ERROR) {
        const data =
          e.endedReason === cast.framework.events.EndedReason.INTERRUPTED
            ? prevCustomData.current
            : customData.current;
        addEvent(data, eventsBI.stop);
        await handleResumeOnStop(data.type, data.isTrailer, e.currentMediaTime);
        e.endedReason === cast.framework.events.EndedReason.END_OF_STREAM && onStop();
      }
      if (
        isError7000.current &&
        e.endedReason === cast.framework.events.EndedReason.INTERRUPTED &&
        e.currentMediaTime === 0
      ) {
        isError7000.current = false;
        playerManager.load(loadRequest.current);
      }
    });
    playerManager.addEventListener(
      cast.framework.events.EventType.PLAYER_LOAD_COMPLETE,
      onLoadComplete
    );

    messageInterceptor.registerCustomListener();
    context.start();
  }, []);

  useEffect(() => {
    clearTimeout(closeTimeout.current);
    if (route === ROUTES.readyToCast) {
      messageInterceptor.broadcast(messages.readyToCast);
      closeTimeout.current = setTimeout(
        () => context.stop(),
        seek.toMilliSeconds(inactivity.close)
      );
      sendEvents();
    }
  }, [route]);

  /**
   * Loads a new stream in the player.
   * @param {Object} loadRequestData Load information coming from the sender.
   * @returns {Object} Request data
   */
  async function onLoad(loadRequestData) {
    switch (loadRequestData.customData.type) {
      case streamType.live: {
        validateSchema('loadMediaLive', loadRequestData);
        setRoute(ROUTES.playerLive);
        setContent({
          liveChannel: loadRequestData.customData.liveChannel,
          schedules: [loadRequestData.customData.liveSchedule]
        });
        break;
      }
      case streamType.recording: {
        validateSchema('loadMediaRecording', loadRequestData);
        setRoute(ROUTES.playerVod);
        setContent({
          logo: loadRequestData.customData.logo || {},
          image: loadRequestData.customData.image,
          title: loadRequestData.customData.title,
          genres: loadRequestData.customData.genres
        });
        break;
      }
      case streamType.vod: {
        if (loadRequestData.customData.isTrailer) {
          validateSchema('loadMediaTrailer', loadRequestData);
        } else {
          validateSchema('loadMediaVod', loadRequestData);
        }
        setRoute(ROUTES.playerVod);
        setContent({
          logo: loadRequestData.customData.logo || {},
          image: loadRequestData.customData.image,
          title: loadRequestData.customData.title,
          genres: loadRequestData.customData.genres
        });
        break;
      }
    }
    youbora.createRealJoinTimeMark();
    prevCustomData.current = customData.current;
    customData.current = loadRequestData.customData;
    loadRequest.current = loadRequestData;
    parental.setPinAlreadyRequested(loadRequestData.customData.pinRequested);
    try {
      return await player.load(loadRequestData);
    } catch (e) {
      handleError(ERROR_TYPES.gvpLoadingData);
    }
  }

  /**
   * Sets the app on ReadyToCast.
   * @param {Object} requestData Request data.
   * @returns {Object} Request data
   */
  function onStop(requestData) {
    startHeartbeatTimeout.current && clearTimeout(startHeartbeatTimeout.current);
    isPinRequired.current = false;
    parental.setPinAlreadyRequested(false);
    setRoute(ROUTES.readyToCast);
    return requestData;
  }

  /**
   * Sets sender's custom data.
   * @param {Object} status Media status.
   * @returns {Object} Media status modified.
   */
  function setCustomData(status) {
    status.customData = {
      pinRequested: parental.getPinAlreadyRequested(),
      title: getTitle(),
      requestingPin: isPinRequired.current,
      isTrailer: customData.current.isTrailer,
      type: customData.current.type,
      pid: customData.current.pid
    };
    log.info('Media status:', status);
    return status;
  }

  /**
   * Broadcasts subtitles and audios to sender
   */
  function onLoadComplete() {
    if (!customData.current.isTrailer) {
      youbora.fireInit();
      // GVP is not invalidating the mobile session for a minute so we have to delay the heartbeat
      // because otherwise the mobile and chromecast would be counted like two devices playing.
      startHeartbeatTimeout.current = setTimeout(() => {
        resume.init(customData.current.type, handleError);
      }, config.getKeyValues().heartBeatWindow * 1000);
    }
    setSubtitles();
    setAudios();
  }

  /**
   * Sets subtitles
   */
  async function setSubtitles() {
    const subtitles = audiosubs.getSubtitles();
    if (
      subtitles.length > 0 &&
      customData.current.type !== streamType.live &&
      !customData.current.isTrailer
    ) {
      const tracks = [];
      let initialSubtitle;
      for (const element of subtitles) {
        initialSubtitle = element.language === customData.current.subtitle && element.language;
        const track = await audiosubs.createTrack(element);
        tracks.push(track);
      }
      audiosubs.addTracks(tracks);
      tracks.push({ trackContentId: null, language: OFF });
      audiosubs.orderByLanguage(tracks, false);
      initialSubtitle ? audiosubs.enableSubtitle(initialSubtitle) : audiosubs.disableSubtitle();
    } else {
      audiosubs.orderByLanguage([{ trackContentId: null, language: OFF }]);
      audiosubs.disableSubtitle();
    }
  }

  /**
   * Sets audios
   */
  function setAudios() {
    // Tests with HLS showed that there were not active track nor racks[0] to be selected.
    try {
      let tracks = playerManager.getAudioTracksManager().getTracks();
      audiosubs.orderByLanguage(tracks, true);
      customData.current.audio
        ? audiosubs.changeAudio(customData.current.audio)
        : audiosubs.sendAudios(
            playerManager.getAudioTracksManager().getActiveTrack()?.language || tracks[0].language,
            customData.current.audio.split('/').length > 1
          );
    } catch {
      // Do nothing.
    }
  }

  /**
   * Sets miniguide according to idle player state
   */
  function setIdleState() {
    setMiniguide(playerStates.idle);
  }

  /**
   * Clears buffering timeout preventing its BI event
   */
  function cancelBufferingEvent() {
    clearTimeout(bufferingTimeout.current);
    bufferingTimeout.current = null;
  }

  /**
   * Sets stop timeouts.
   * @param {number} delay Time to delay.
   * @param {boolean} isClose App must be shutdown.
   */
  function setStopTimeout(delay, isClose) {
    clearTimeout(stopTimeout.current);
    stopTimeout.current = setTimeout(async () => {
      messageInterceptor.broadcast(messages.expiredRequestPin);
      await handleResumeOnStop(customData.current.type, customData.current.isTrailer);
      playerManager.stop();
      setRoute(ROUTES.readyToCast);
      isClose && context.stop();
    }, seek.toMilliSeconds(delay));
  }

  /**
   * Handles resume and heart beat on stop.
   * @param {string} type Live/VoD.
   * @param {boolean} isTrailer Trailer or not.
   * @param {number|null} currentTime Time played.
   */
  async function handleResumeOnStop(type, isTrailer, currentTime) {
    type !== streamType.live && !isTrailer && (await resume.updateResume(currentTime));
    resume.stopHeartBeat();
  }

  /**
   * Manages parental PIN.
   */
  function handleParental() {
    isPinRequired.current = false;
    const route = parental.checkParentalControl();
    setRoute(route);
  }

  /**
   * Handle error.
   * @param {string} errorType Error type.
   * @param {string} subtext Subtext.
   */
  function handleError(errorType, subtext = '') {
    const error = { id: errorType, msg: translate(errorType) };
    setError({ text: errorType, subtext });
    setRoute(ROUTES.error);
    playerManager.stop();
    resume.stopHeartBeat();
    addEvent(customData.current, eventsBI.error);
    validateSchema('error', error);
    messageInterceptor.broadcast(messages.error, error);
  }

  /**
   * Refreshes programs.
   * @param {Array} programs New programs.
   */
  function refreshPrograms(programs) {
    seek.setStartTime(programs[0].start);
    setTitle(programs[0].title);
    setContent((prev) => ({ ...prev, schedules: programs }));
  }

  return (
    <div
      className={classNames([
        styles.wrapper,
        styles.bg,
        styles[`bg${skin}`],
        [ROUTES.playerLive, ROUTES.playerVod].includes(route) && styles.bgPlayer
      ])}
    >
      {route === ROUTES.splash && <Splash skin={skin} />}
      {route === ROUTES.readyToCast && <ReadyToCast skin={skin} />}
      {(route === ROUTES.playerVod || route === ROUTES.playerLive) && content && (
        <PlayerBar
          isVod={route === ROUTES.playerVod}
          playerState={miniguide}
          setIdleState={setIdleState}
          setIsPinRequired={(required) => (isPinRequired.current = required)}
          handleError={handleError}
          refreshPrograms={refreshPrograms}
          setRoute={setRoute}
          content={content}
        />
      )}
      {route === ROUTES.error && <Error error={error} setRoute={setRoute} />}
      {route === ROUTES.pin && content && <ReadyToPin currentSchedule={content.schedules[0]} />}
      {route === ROUTES.goToLive && <GoToLive setRoute={setRoute} />}
    </div>
  );
};
